import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Search,
  Database,
  MapPin,
  Clock,
  ChevronLeft,
  ChevronRight,
  Link2,
  AlertTriangle,
  Activity,
  Layers,
  Loader2,
  Sparkles,
  X,
  DollarSign,
  Trash2,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import SubscriptionFilter from "@/components/SubscriptionFilter";

export default function ResourcesPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [resources, setResources] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "active");
  const [subscriptionId, setSubscriptionId] = useState(searchParams.get("subscription") || "");
  const [locationFilter, setLocationFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState(searchParams.get("type") || "");
  const [rgFilter, setRgFilter] = useState(searchParams.get("rg") || "");
  const [page, setPage] = useState(0);
  const [selectedResource, setSelectedResource] = useState(null);
  const [relatedData, setRelatedData] = useState(null);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [filters, setFilters] = useState(null);
  const [nlResult, setNlResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [textSearch, setTextSearch] = useState("");
  const [orphanIds, setOrphanIds] = useState(new Set());
  const [orphanCosts, setOrphanCosts] = useState({});
  const [costThreshold, setCostThreshold] = useState(50);
  const [quickStats, setQuickStats] = useState(null);
  const searchRef = useRef(null);
  const limit = 20;

  useEffect(() => {
    api()
      .get("/resources-meta/filters")
      .then((res) => setFilters(res.data))
      .catch(() => {});
    api()
      .get("/resources/analysis/orphan-costs")
      .then((res) => {
        setOrphanCosts(res.data.orphans || {});
        setOrphanIds(new Set(Object.keys(res.data.orphans || {})));
        setCostThreshold(res.data.threshold || 50);
      })
      .catch(() => {
        // Fallback to basic orphan IDs
        api()
          .get("/resources/analysis/orphan-ids")
          .then((res) => setOrphanIds(new Set(res.data.orphan_ids)))
          .catch(() => {});
      });
    // Fetch quick stats for context card
    Promise.allSettled([
      api().get("/resources-meta/filters"),
      api().get("/changes/summary?days=7"),
    ]).then(([filtersRes, changesRes]) => {
      const stats = {};
      if (filtersRes.status === "fulfilled") {
        stats.totalTypes = filtersRes.value.data?.types?.length || 0;
        stats.totalLocations = filtersRes.value.data?.locations?.length || 0;
        stats.totalRGs = filtersRes.value.data?.resource_groups?.length || 0;
      }
      if (changesRes.status === "fulfilled") {
        stats.changesThisWeek = changesRes.value.data?.this_week || 0;
        stats.orphansDetected = changesRes.value.data?.orphans?.detected || 0;
      }
      setQuickStats(stats);
    });
  }, [api]);

  const fetchResources = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: (page * limit).toString(),
      });
      if (textSearch) params.append("search", textSearch);
      if (statusFilter) params.append("status", statusFilter);
      if (subscriptionId) params.append("subscription_id", subscriptionId);
      if (locationFilter) params.append("location", locationFilter);
      if (typeFilter) params.append("resource_type", typeFilter);
      if (rgFilter) params.append("resource_group", rgFilter);

      const res = await api().get(`/resources?${params}`);
      setResources(res.data.resources);
      setTotal(res.data.total);
    } catch (err) {
      toast.error("Failed to fetch resources");
    } finally {
      setLoading(false);
    }
  }, [api, page, textSearch, statusFilter, subscriptionId, locationFilter, typeFilter, rgFilter]);

  // Only auto-fetch when NOT in NL mode and NOT searching
  useEffect(() => {
    if (!nlResult && !searching) {
      const timer = setTimeout(fetchResources, 300);
      return () => clearTimeout(timer);
    }
  }, [fetchResources, nlResult, searching]);

  // Deep-link: auto-trigger orphan or untagged view from URL params
  const deepLinked = useRef(false);
  useEffect(() => {
    if (deepLinked.current) return;
    const view = searchParams.get("view") || searchParams.get("status");
    if (!view || !api) return;
    deepLinked.current = true;

    if (view === "orphan" || view === "orphans") {
      setSearching(true);
      api().get("/resources/analysis/orphans")
        .then((res) => {
          const orphanList = res.data.orphans || [];
          setNlResult({
            intent: "orphans",
            explanation: `Found ${res.data.total_orphans} orphaned resources (${res.data.orphan_rate}% orphan rate)`,
            resources: orphanList,
            total: res.data.total_orphans,
            estimated_savings: 0,
          });
          setResources(orphanList);
          setTotal(res.data.total_orphans);
        })
        .catch(() => {})
        .finally(() => setSearching(false));
    } else {
      const queryMap = { untagged: "show all untagged resources" };
      const nlQuery = queryMap[view];
      if (nlQuery) {
        setQuery(nlQuery);
        setSearching(true);
        api().post("/resources/nl-query", { query: nlQuery })
          .then((res) => {
            setNlResult(res.data);
            if (res.data.intent === "filter" || res.data.intent === "orphans") {
              setResources(res.data.resources || []);
              setTotal(res.data.total || 0);
            }
          })
          .catch(() => {})
          .finally(() => setSearching(false));
      }
    }
  }, [searchParams, api]);


  const isNaturalLanguage = (q) => {
    if (!q || q.length < 5) return false;
    const nlIndicators = [
      "show", "find", "which", "what", "list", "orphan", "unused", "cost",
      "without", "missing", "most", "expensive", "unattached", "delete",
    ];
    const lower = q.toLowerCase();
    return nlIndicators.some((w) => lower.includes(w));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) {
      clearSearch();
      return;
    }

    if (isNaturalLanguage(q)) {
      // AI-powered search — block regular fetches
      setSearching(true);
      try {
        const body = { query: q };
        if (subscriptionId) body.subscription_id = subscriptionId;
        const res = await api().post("/resources/nl-query", body);
        setNlResult(res.data);
        if (res.data.intent === "filter" || res.data.intent === "orphans") {
          setResources(res.data.resources || []);
          setTotal(res.data.total || 0);
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || "Search failed");
      } finally {
        setSearching(false);
      }
    } else {
      // Regular text search — update textSearch to trigger fetchResources
      setNlResult(null);
      setTextSearch(q);
      setPage(0);
    }
  };

  const clearSearch = () => {
    setQuery("");
    setTextSearch("");
    setNlResult(null);
    setSearching(false);
    setPage(0);
    searchRef.current?.focus();
  };

  const openResourceDetail = async (resource) => {
    setSelectedResource(resource);
    setRelatedData(null);
    setLoadingRelated(true);
    try {
      const res = await api().get(`/resources/${resource.id}/related`);
      setRelatedData(res.data);
    } catch (err) {
      console.error("Failed to fetch related:", err);
    } finally {
      setLoadingRelated(false);
    }
  };

  const resetFilters = () => {
    setQuery("");
    setTextSearch("");
    setStatusFilter("active");
    setSubscriptionId("");
    setLocationFilter("");
    setTypeFilter("");
    setRgFilter("");
    setNlResult(null);
    setSearching(false);
    setPage(0);
  };

  const totalPages = Math.ceil(total / limit);
  const hasFilters = locationFilter || typeFilter || rgFilter || subscriptionId;
  const isNlMode = !!nlResult;

  return (
    <div className="space-y-6" data-testid="resources-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">
            Resource Explorer
          </h1>
          <p className="text-[11px] text-[var(--n-text-muted)] mt-0.5">
            Search, filter, and explore Azure resource relationships
          </p>
        </div>
        {quickStats && (
          <div className="flex items-center gap-4 text-xs">
            <span className="text-[var(--n-text-muted)]"><span className="text-foreground font-mono font-bold">{quickStats.totalTypes || 0}</span> types</span>
            <span className="text-[var(--n-text-muted)]"><span className="text-foreground font-mono font-bold">{quickStats.totalRGs || 0}</span> RGs</span>
            {quickStats.changesThisWeek > 0 && <span className="text-[var(--hig-accent)]"><span className="font-mono font-bold">{quickStats.changesThisWeek}</span> changes/7d</span>}
            {orphanIds.size > 0 && <button onClick={() => {
              if (isNlMode) return;
              setSearching(true);
              const params = subscriptionId ? `?subscription_id=${subscriptionId}` : "";
              api().get(`/resources/analysis/orphans${params}`)
                .then((res) => {
                  const orphanList = res.data.orphans || [];
                  setNlResult({ intent: "orphans", explanation: `Found ${res.data.total_orphans} orphaned resources (${res.data.orphan_rate}% orphan rate)`, resources: orphanList, total: res.data.total_orphans, estimated_savings: 0 });
                  setResources(orphanList);
                  setTotal(res.data.total_orphans);
                })
                .catch(() => {})
                .finally(() => setSearching(false));
            }} className="text-amber-600 dark:text-amber-400 hover:underline cursor-pointer" data-testid="header-orphan-stat"><span className="font-mono font-bold">{orphanIds.size}</span> orphans</button>}
          </div>
        )}
      </div>

      {/* Unified Search Bar */}
      <form onSubmit={handleSearch} className="relative" data-testid="search-form">
        <div className="flex gap-2">
          <div className="relative flex-1">
            {searching ? (
              <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--hig-accent)] animate-spin" />
            ) : (
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            )}
            <Input
              ref={searchRef}
              placeholder='Search by name, type, RG... or ask: "show orphaned snapshots", "which RGs cost the most"'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="search-input"
              className="pl-9 pr-10 bg-[var(--n-card)] border-[var(--n-border-2)] text-sm h-10"
            />
            {query && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground transition-colors"
                data-testid="clear-search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <Button
            type="submit"
            disabled={searching}
            className={`shrink-0 h-10 px-4 font-semibold ${
              query && isNaturalLanguage(query)
                ? "bg-[var(--hig-accent)] hover:opacity-90 text-primary-foreground"
                : "bg-[var(--n-border-2)] hover:bg-[#2a2a30] text-foreground"
            }`}
            data-testid="search-btn"
          >
            {query && isNaturalLanguage(query) ? (
              <Sparkles className="w-4 h-4" />
            ) : (
              <Search className="w-4 h-4" />
            )}
          </Button>
        </div>
      </form>

      {/* NL Result Feedback Banner */}
      {nlResult && (
        <div
          className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-xs animate-fade-in ${
            nlResult.intent === "orphans"
              ? "bg-amber-500/5 border border-amber-500/20"
              : nlResult.intent === "cost"
                ? "bg-emerald-500/5 border border-emerald-500/20"
                : "bg-[var(--hig-accent)]/5 border border-[var(--hig-accent)]/20"
          }`}
          data-testid="search-result-banner"
        >
          {nlResult.intent === "orphans" ? (
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
          ) : nlResult.intent === "cost" ? (
            <DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
          ) : (
            <Sparkles className="w-4 h-4 text-[var(--hig-accent)] shrink-0" />
          )}
          <span className={
            nlResult.intent === "orphans" ? "text-amber-600 dark:text-amber-400" :
            nlResult.intent === "cost" ? "text-emerald-600 dark:text-emerald-400" : "text-[var(--hig-accent)]"
          }>
            {nlResult.explanation}
          </span>
          <span className="text-muted-foreground ml-1">
            ({nlResult.total || nlResult.resources?.length || 0} results)
          </span>
          {nlResult.estimated_savings > 0 && (
            <Badge variant="outline" className="text-[10px] border-emerald-500/20 text-emerald-600 dark:text-emerald-400 ml-1 shrink-0">
              ~USD {nlResult.estimated_savings.toLocaleString()} potential savings
            </Badge>
          )}
          {nlResult.intent === "orphans" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/cleanup")}
              className="h-6 text-[10px] text-amber-600 dark:text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 shrink-0"
              data-testid="go-to-cleanup-wizard"
            >
              <Trash2 className="w-3 h-3 mr-1" /> Cleanup Wizard
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={clearSearch}
            className="ml-auto h-6 text-[10px] text-muted-foreground hover:text-foreground"
            data-testid="clear-nl-results"
          >
            Show all resources
          </Button>
        </div>
      )}

      {/* Cost Results (for NL cost queries) */}
      {nlResult?.intent === "cost" && nlResult.costs && (
        <Card className="bg-[var(--n-card)] border-[var(--n-border-2)] animate-fade-in">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Total Cost</span>
              <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
                USD {nlResult.total_cost?.toLocaleString()}
              </span>
            </div>
            {nlResult.costs.slice(0, 8).map((c, i) => {
              const max = nlResult.costs[0]?.cost || 1;
              return (
                <div key={i} className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-foreground truncate max-w-[60%]">{c.resource_group}</span>
                    <span className="font-mono text-foreground">{c.currency} {c.cost.toLocaleString()}</span>
                  </div>
                  <div className="h-1 bg-[var(--n-border-2)] rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(c.cost / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-2">
        <SubscriptionFilter
          value={subscriptionId}
          onChange={(v) => { setSubscriptionId(v); setPage(0); setNlResult(null); }}
        />
        {filters?.locations?.length > 0 && (
          <Select value={locationFilter || "all"} onValueChange={(v) => { setLocationFilter(v === "all" ? "" : v); setPage(0); setNlResult(null); }}>
            <SelectTrigger className="w-32 sm:w-40 bg-[var(--n-card)] border-[var(--n-border-2)] text-xs h-8" data-testid="location-filter">
              <MapPin className="w-3 h-3 mr-1 text-muted-foreground shrink-0" />
              <SelectValue placeholder="Location" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {filters.locations.map((l) => (
                <SelectItem key={l} value={l}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {filters?.types?.length > 0 && (
          <Select value={typeFilter || "all"} onValueChange={(v) => { setTypeFilter(v === "all" ? "" : v); setPage(0); setNlResult(null); }}>
            <SelectTrigger className="w-36 sm:w-48 bg-[var(--n-card)] border-[var(--n-border-2)] text-xs h-8" data-testid="type-filter">
              <Database className="w-3 h-3 mr-1 text-muted-foreground shrink-0" />
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {filters.types.slice(0, 30).map((t) => (
                <SelectItem key={t.type} value={t.type}>
                  {t.type.split("/").pop()} ({t.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {filters?.resource_groups?.length > 0 && (
          <Select value={rgFilter || "all"} onValueChange={(v) => { setRgFilter(v === "all" ? "" : v); setPage(0); setNlResult(null); }}>
            <SelectTrigger className="w-36 sm:w-48 bg-[var(--n-card)] border-[var(--n-border-2)] text-xs h-8" data-testid="rg-filter">
              <Layers className="w-3 h-3 mr-1 text-muted-foreground shrink-0" />
              <SelectValue placeholder="Resource Group" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All RGs</SelectItem>
              {filters.resource_groups.map((rg) => (
                <SelectItem key={rg} value={rg}>{rg}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); setNlResult(null); }}>
          <SelectTrigger className="w-28 bg-[var(--n-card)] border-[var(--n-border-2)] text-xs h-8" data-testid="status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="deleted">Deleted</SelectItem>
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={resetFilters} className="text-xs text-muted-foreground h-8" data-testid="clear-filters-btn">
            Clear all
          </Button>
        )}
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span data-testid="result-count">
          {loading ? "Loading..." : `${total} resource${total !== 1 ? "s" : ""}`}
          {isNlMode && " (AI search)"}
        </span>
        {!isNlMode && orphanIds.size > 0 && (
          <button
            onClick={() => {
              setSearching(true);
              const params = subscriptionId ? `?subscription_id=${subscriptionId}` : "";
              api().get(`/resources/analysis/orphans${params}`)
                .then((res) => {
                  const orphanList = res.data.orphans || [];
                  setNlResult({
                    intent: "orphans",
                    explanation: `Found ${res.data.total_orphans} orphaned resources (${res.data.orphan_rate}% orphan rate)`,
                    resources: orphanList,
                    total: res.data.total_orphans,
                    estimated_savings: 0,
                  });
                  setResources(orphanList);
                  setTotal(res.data.total_orphans);
                })
                .catch((err) => toast.error(err.response?.data?.detail || "Failed to load orphans"))
                .finally(() => setSearching(false));
            }}
            className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400/80 hover:text-amber-600 dark:text-amber-400 transition-colors"
            data-testid="orphan-count-indicator"
          >
            <AlertTriangle className="w-3 h-3" />
            <span>{orphanIds.size} orphan{orphanIds.size !== 1 ? "s" : ""} detected</span>
          </button>
        )}
      </div>

      {/* Resource Table */}
      <Card className="bg-[var(--n-card)] border-[var(--n-border-2)]">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-[var(--n-border-2)] hover:bg-transparent">
                <TableHead className="text-muted-foreground text-xs">Name</TableHead>
                <TableHead className="text-muted-foreground text-xs hidden md:table-cell">Type</TableHead>
                <TableHead className="text-muted-foreground text-xs hidden lg:table-cell">Resource Group</TableHead>
                <TableHead className="text-muted-foreground text-xs hidden sm:table-cell">Location</TableHead>
                <TableHead className="text-muted-foreground text-xs hidden xl:table-cell">Tags</TableHead>
                <TableHead className="text-muted-foreground text-xs">Last Seen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading || searching ? (
                [...Array(6)].map((_, i) => (
                  <TableRow key={i} className="border-[var(--n-border-2)]">
                    <TableCell colSpan={6}>
                      <div className="h-8 bg-[var(--n-border-2)] rounded animate-pulse" />
                    </TableCell>
                  </TableRow>
                ))
              ) : resources.length === 0 ? (
                <TableRow className="border-[var(--n-border-2)]">
                  <TableCell colSpan={6} className="text-center py-12">
                    <Database className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No resources found</p>
                    {isNlMode && (
                      <Button variant="ghost" size="sm" onClick={clearSearch} className="mt-2 text-[var(--hig-accent)] text-xs">
                        Show all resources
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                resources.map((r) => {
                  const isOrphan = orphanIds.has(r.id);
                  const orphanInfo = orphanCosts[r.id];
                  const isHighCost = orphanInfo?.high_cost;
                  const orphanColor = isHighCost ? "red" : "amber";
                  return (
                  <TableRow
                    key={r.id}
                    className={`border-[var(--n-border-2)] cursor-pointer transition-colors ${
                      isOrphan
                        ? isHighCost
                          ? "hover:bg-red-500/[0.06] bg-red-500/[0.02]"
                          : "hover:bg-amber-500/[0.06] bg-amber-500/[0.02]"
                        : "hover:bg-[var(--n-hover)]"
                    }`}
                    onClick={() => openResourceDetail(r)}
                    data-testid={`resource-row-${r.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        {isOrphan && (
                          <span
                            className={`flex items-center justify-center w-5 h-5 rounded shrink-0 ${
                              isHighCost ? "bg-red-500/10" : "bg-amber-500/10"
                            }`}
                            title={`Orphaned${orphanInfo ? ` — ~$${orphanInfo.estimated_cost}/mo` : ""}`}
                          >
                            <AlertTriangle className={`w-3 h-3 ${isHighCost ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400"}`} />
                          </span>
                        )}
                        <div className="min-w-0">
                          <p className={`text-sm font-medium truncate max-w-[180px] sm:max-w-[280px] ${
                            isOrphan ? (isHighCost ? "text-red-700 dark:text-red-200" : "text-amber-700 dark:text-amber-200") : "text-foreground"
                          }`}>{r.name}</p>
                          <p className="text-[10px] text-muted-foreground md:hidden">{r.type?.split("/").pop()}</p>
                        </div>
                        {isOrphan && orphanInfo?.owner_email && (
                          <a
                            href={`mailto:${orphanInfo.owner_email}?subject=${encodeURIComponent(`Azure Orphaned Resource: ${r.name}`)}&body=${encodeURIComponent(
                              `Hi ${orphanInfo.owner_name || "Team"},\n\nThe following Azure resource appears to be orphaned and may be incurring unnecessary costs:\n\n• Resource: ${r.name}\n• Type: ${r.type}\n• Resource Group: ${r.resource_group}\n• Estimated Cost: ~$${orphanInfo.estimated_cost || 0}/month\n• Reason: ${orphanInfo.reason || "No detected associations"}\n\nCould you please review whether this resource is still needed? If not, it can be safely deleted to reduce costs.\n\nThank you`
                            )}`}
                            onClick={(e) => e.stopPropagation()}
                            className={`shrink-0 p-1 rounded transition-colors ${
                              isHighCost
                                ? "text-red-600 dark:text-red-400/60 hover:text-red-600 dark:text-red-400 hover:bg-red-500/10"
                                : "text-amber-600 dark:text-amber-400/60 hover:text-amber-600 dark:text-amber-400 hover:bg-amber-500/10"
                            }`}
                            title={`Email ${orphanInfo.owner_name || orphanInfo.owner_email}`}
                            data-testid={`notify-orphan-${r.id}`}
                          >
                            <Mail className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-xs text-muted-foreground font-mono">{r.type?.split("/").pop()}</span>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-xs text-muted-foreground truncate max-w-[160px] block">{r.resource_group}</span>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <span className="text-xs text-muted-foreground">{r.location}</span>
                    </TableCell>
                    <TableCell className="hidden xl:table-cell">
                      {r.tags && Object.keys(r.tags).length > 0 ? (
                        <div className="flex gap-1 flex-wrap max-w-[180px]">
                          {Object.entries(r.tags).slice(0, 2).map(([k, v]) => (
                            <Badge key={k} variant="outline" className="text-[10px] border-[var(--n-border-2)] text-muted-foreground px-1.5 py-0">
                              {k}: {String(v).slice(0, 12)}
                            </Badge>
                          ))}
                          {Object.keys(r.tags).length > 2 && (
                            <Badge variant="outline" className="text-[10px] border-[var(--n-border-2)] text-muted-foreground px-1.5 py-0">
                              +{Object.keys(r.tags).length - 2}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-[10px] text-muted-foreground/40">No tags</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {r.last_seen ? new Date(r.last_seen).toLocaleDateString() : "-"}
                      </span>
                    </TableCell>
                  </TableRow>
                );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {!isNlMode && totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--n-border-2)]">
            <span className="text-xs text-muted-foreground">
              {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="border-[var(--n-border-2)] text-xs h-7" data-testid="prev-page-btn">
                <ChevronLeft className="w-3 h-3" />
              </Button>
              <span className="text-xs text-muted-foreground">{page + 1}/{totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} className="border-[var(--n-border-2)] text-xs h-7" data-testid="next-page-btn">
                <ChevronRight className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Resource Detail Sheet (Drawer) */}
      <Sheet open={!!selectedResource} onOpenChange={() => setSelectedResource(null)}>
        <SheetContent
          side="right"
          className="bg-[var(--n-card)] border-l border-[var(--n-border-2)] !w-full !max-w-[480px] overflow-y-auto p-0"
        >
          {selectedResource && (
            <>
              <SheetHeader className="p-4 pb-3 border-b border-[var(--n-border-2)] sticky top-0 bg-[var(--n-card)] z-10">
                <SheetTitle className="font-heading text-base" data-testid="resource-detail-title">
                  {selectedResource.name}
                </SheetTitle>
                <SheetDescription className="text-muted-foreground font-mono text-xs">
                  {selectedResource.type?.split("/").pop()}
                </SheetDescription>
              </SheetHeader>

              {/* Orphan Warning */}
              {relatedData?.is_orphan && (
                <div className="mx-4 mt-3 flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 text-xs" data-testid="orphan-warning">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="text-amber-700 dark:text-amber-400 font-medium">Potentially Orphaned</span>
                    <p className="text-amber-600 dark:text-amber-400/80 mt-0.5">{relatedData.orphan_info?.reason}</p>
                    <p className="text-amber-600/80 dark:text-amber-400/60 mt-0.5 text-[10px]">{relatedData.orphan_info?.action}</p>
                  </div>
                </div>
              )}

              <Tabs defaultValue="details" className="flex-1">
                <TabsList className="bg-[var(--n-bg)] border-b border-[var(--n-border-2)] w-full rounded-none justify-start px-4 h-9">
                  <TabsTrigger value="details" className="text-xs data-[state=active]:bg-transparent data-[state=active]:text-[var(--hig-accent)] data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[var(--hig-accent)] rounded-none pb-2">Details</TabsTrigger>
                  <TabsTrigger value="related" className="text-xs data-[state=active]:bg-transparent data-[state=active]:text-[var(--hig-accent)] data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[var(--hig-accent)] rounded-none pb-2" data-testid="related-tab">
                    Related {relatedData ? `(${relatedData.related?.length || 0})` : ""}
                  </TabsTrigger>
                  <TabsTrigger value="context" className="text-xs data-[state=active]:bg-transparent data-[state=active]:text-[var(--hig-accent)] data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[var(--hig-accent)] rounded-none pb-2">Context</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="p-4 space-y-4 mt-0">
                  <div className="grid grid-cols-2 gap-3">
                    <InfoField label="Location" value={selectedResource.location} icon={<MapPin className="w-3 h-3" />} />
                    <InfoField label="Resource Group" value={selectedResource.resource_group} icon={<Layers className="w-3 h-3" />} />
                    <InfoField label="Status" value={
                      <Badge variant="outline" className={`text-[10px] ${selectedResource.status === "active" ? "text-emerald-600 dark:text-emerald-400 border-emerald-500/20" : "text-red-600 dark:text-red-400 border-red-500/20"}`}>
                        {selectedResource.status}
                      </Badge>
                    } />
                    <InfoField label="Created" value={selectedResource.created_time ? new Date(selectedResource.created_time).toLocaleDateString() : "-"} icon={<Clock className="w-3 h-3" />} />
                    <InfoField label="Last Changed" value={selectedResource.changed_time ? new Date(selectedResource.changed_time).toLocaleDateString() : "-"} />
                    <InfoField label="Last Seen" value={selectedResource.last_seen ? new Date(selectedResource.last_seen).toLocaleDateString() : "-"} />
                  </div>

                  {selectedResource.tags && Object.keys(selectedResource.tags).length > 0 && (
                    <div>
                      <p className="text-[10px] text-muted-foreground mb-1.5 uppercase tracking-wider">Tags</p>
                      <div className="flex flex-wrap gap-1.5">
                        {Object.entries(selectedResource.tags).map(([k, v]) => (
                          <Badge key={k} variant="outline" className="text-xs border-[var(--hig-accent)]/30 dark:border-[var(--hig-accent)]/20 text-[var(--hig-accent)] dark:text-[var(--hig-accent)] px-2 py-0.5">
                            {k}: {v}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="text-[10px] text-muted-foreground mb-1.5 uppercase tracking-wider">Subscription</p>
                    <p className="text-[10px] font-mono text-muted-foreground break-all">{selectedResource.subscription_id}</p>
                  </div>

                  {selectedResource.azure_id && (
                    <div>
                      <p className="text-[10px] text-muted-foreground mb-1.5 uppercase tracking-wider">Azure ID</p>
                      <p className="text-[10px] font-mono text-muted-foreground break-all bg-[var(--n-bg)] p-2 rounded border border-[var(--n-border-2)]">
                        {selectedResource.azure_id}
                      </p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="related" className="p-4 mt-0">
                  {loadingRelated ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-5 h-5 animate-spin text-[var(--hig-accent)]" />
                    </div>
                  ) : relatedData?.related?.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-[10px] text-muted-foreground mb-2 uppercase tracking-wider">Direct Relationships</p>
                      {relatedData.related.map((r, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2.5 p-2.5 rounded-lg bg-[var(--n-bg)] border border-[var(--n-border-2)] hover:border-[var(--hig-accent)]/20 cursor-pointer transition-colors"
                          onClick={() => {
                            setSelectedResource(null);
                            setTimeout(async () => {
                              try {
                                const res = await api().get(`/resources?search=${encodeURIComponent(r.name)}&limit=1`);
                                if (res.data.resources?.[0]) openResourceDetail(res.data.resources[0]);
                              } catch {}
                            }, 200);
                          }}
                          data-testid={`related-resource-${i}`}
                        >
                          <Link2 className="w-3.5 h-3.5 text-[var(--hig-accent)] shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate">{r.name}</p>
                            <p className="text-[10px] text-muted-foreground font-mono">{r.type.split("/").pop()}</p>
                          </div>
                          <Badge variant="outline" className="text-[10px] border-[var(--hig-accent)]/20 text-[var(--hig-accent)] shrink-0">
                            {r.relationship.replace(/_/g, " ")}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Link2 className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No direct relationships detected</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="context" className="p-4 space-y-4 mt-0">
                  {relatedData?.events?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-muted-foreground mb-2 uppercase tracking-wider flex items-center gap-1">
                        <Activity className="w-3 h-3" /> Timeline
                      </p>
                      <div className="space-y-1.5">
                        {relatedData.events.map((e, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs p-2 rounded bg-[var(--n-bg)]">
                            <Badge variant="outline" className={`text-[10px] shrink-0 ${
                              e.event_type === "created" ? "text-emerald-600 dark:text-emerald-400 border-emerald-500/20" :
                              e.event_type === "deleted" ? "text-red-600 dark:text-red-400 border-red-500/20" :
                              "text-amber-600 dark:text-amber-400 border-amber-500/20"
                            }`}>{e.event_type}</Badge>
                            <span className="text-muted-foreground truncate">{e.details}</span>
                            <span className="text-muted-foreground shrink-0">{new Date(e.timestamp).toLocaleDateString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {relatedData?.same_resource_group?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-muted-foreground mb-2 uppercase tracking-wider flex items-center gap-1">
                        <Layers className="w-3 h-3" /> Same Resource Group ({relatedData.same_resource_group.length})
                      </p>
                      <div className="space-y-1 max-h-60 overflow-y-auto">
                        {relatedData.same_resource_group.map((r, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs p-1.5 rounded bg-[var(--n-bg)]">
                            <span className="text-foreground truncate">{r.name}</span>
                            <span className="text-muted-foreground font-mono text-[10px] shrink-0">{r.type.split("/").pop()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {!relatedData?.events?.length && !relatedData?.same_resource_group?.length && (
                    <div className="text-center py-12">
                      <Activity className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No context available</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function InfoField({ label, value, icon }) {
  return (
    <div>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">{label}</p>
      <div className="text-xs text-foreground flex items-center gap-1">
        {icon && <span className="text-muted-foreground">{icon}</span>}
        {typeof value === "string" ? <span className="truncate">{value}</span> : value}
      </div>
    </div>
  );
}
